import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { AuditDetail } from '@/features/external-users/types/audit-log.types';

/**
 * Les détails lisibles d'une ligne d'historique, tels que l'API les a mis en
 * forme. Le front n'en traduit rien : libellés et valeurs arrivent prêts.
 */
export const AuditDetailsList: React.FC<{ details: AuditDetail[] }> = ({ details }) => {
  if (details.length === 0) return null;
  return (
    <dl className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2">
      {details.map((detail, index) => (
        <div key={`${detail.label}-${index}`} className="tw-flex tw-flex-col">
          <dt className="tw-text-xs tw-font-medium tw-text-gray-500">{detail.label}</dt>
          <dd className="tw-text-sm tw-text-gray-900 tw-mt-0.5 tw-break-words">
            {detail.value !== undefined ? (
              detail.value
            ) : (
              <>
                <span className="tw-text-gray-500">{detail.before}</span>
                <span className="tw-mx-1.5 tw-text-gray-400">→</span>
                <span className="tw-font-medium tw-text-green-700">{detail.after}</span>
              </>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * Bloc replié des données brutes : identifiants Stripe, résumé de développeur,
 * étapes techniques. Utile au support et aux développeurs, jamais nécessaire
 * pour comprendre ce qui s'est passé.
 */
export const AuditTechnicalBlock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOuvert(!ouvert)}
        className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors"
      >
        {ouvert ? <ChevronDown className="tw-w-3 tw-h-3" /> : <ChevronRight className="tw-w-3 tw-h-3" />}
        <span>Informations techniques</span>
      </button>
      {ouvert && <div className="tw-mt-2">{children}</div>}
    </div>
  );
};

export const AuditTechnicalData: React.FC<{ data: Record<string, unknown> }> = ({ data }) => (
  <pre className="tw-text-xs tw-text-gray-600 tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded tw-p-2 tw-overflow-x-auto tw-whitespace-pre-wrap tw-break-all tw-m-0">
    {JSON.stringify(data, null, 2)}
  </pre>
);
